/**
 * computePlayerTypology — pedagogical profiling engine for the
 * "Fiche PDF joueur" feature (04/06/2026, sprint feedback hst-game.com).
 *
 * Input: a `snapshot` object (the same one sent by `useTelemetryReporter`).
 *
 * Output: {
 *   scores: { riskTaking, finance, hr, banking, training, support, crm, rse },
 *           // 8 axes, each 0..10 (rounded to 0.5)
 *   typology: { id, label, summary },
 *           // e.g. { id: 'visionnaire-prudent', label: 'Visionnaire prudent', summary: '…' }
 *   analysis: string[],
 *           // 3-5 short pedagogical sentences (FR), rule-based
 *   highlights: { positives: string[], improvements: string[] },
 *           // bullet points for the PDF (max 3 each)
 * }
 *
 * Design rationale: rules-based (not LLM) so the report can be generated
 * offline, instantly, and remain deterministic for the user request:
 * "Analyse écrite — Générée par règles (templates conditionnels)".
 */

/* ─── Score utilities ─────────────────────────────────────────────────── */

function clamp01(x) { return Math.max(0, Math.min(10, x)); }
function round05(x) { return Math.round(x * 2) / 2; }
function score(x) { return round05(clamp01(x)); }

/** Maps a value to 0..10 with [min → 0, mid → 5, max → 10] linear segments. */
function rangeScore(v, min, mid, max) {
  if (!Number.isFinite(v)) return 0;
  if (v <= min) return 0;
  if (v >= max) return 10;
  if (v <= mid) return ((v - min) / (mid - min)) * 5;
  return 5 + ((v - mid) / (max - mid)) * 5;
}

/* ─── Per-axis evaluators ─────────────────────────────────────────────── */

function evalRiskTaking(s) {
  // High = aggressive expansion (debt + leasing + leveraged growth).
  // Low = conservative (cash hoarder, small fleet, no debt).
  const dayCount = Math.max(1, s.total_days || 1);
  const debtRatio = (s.credits_total_remaining || 0) / Math.max(1000, s.monthly_revenue || 1000);
  const leasedShare = (s.fleet_leased || 0) / Math.max(1, s.fleet_total || 1);
  const fleetVelocity = (s.fleet_total || 0) / Math.max(30, dayCount) * 30; // fleet per month
  // Each component capped, summed, normalised.
  const a = rangeScore(debtRatio, 0, 3, 8);
  const b = rangeScore(leasedShare * 10, 0, 4, 8);
  const c = rangeScore(fleetVelocity, 0, 0.5, 2);
  return score((a + b + c) / 3);
}

function evalFinance(s) {
  // Treasury runway + equity health + net margin.
  const burn = Math.max(1, s.monthly_expenses || 1);
  const runway = (s.treasury || 0) / burn;
  const equity = s.equity || 0;
  const net = (s.monthly_revenue || 0) - (s.monthly_expenses || 0);
  const margin = net / Math.max(1, s.monthly_revenue || 1);
  const a = rangeScore(runway, 0, 3, 12);       // 0=insolvent → 10=12+ months runway
  const b = equity < 0 ? 0 : rangeScore(equity, 0, 50000, 300000);
  const c = rangeScore(margin * 100, -10, 8, 25); // %
  return score((a + b + c) / 3);
}

function evalHR(s) {
  // Satisfaction + driver count growth + fatigue control.
  const sat = s.drivers_avg_satisfaction || 0;
  const fatigue = s.drivers_avg_fatigue || 0;
  const a = rangeScore(sat, 30, 65, 90);
  const b = rangeScore(100 - fatigue, 30, 70, 95);
  const c = rangeScore(s.drivers_total || 0, 0, 4, 12);
  return score((a + b + c) / 3);
}

function evalBanking(s) {
  // Credit score (BNB) + bank loan usage (some is good, too much is bad).
  // No data = neutral 5.
  const cs = s.credit_score;
  const csScore = (cs == null) ? 5 : rangeScore(cs, 30, 60, 85);
  const creditCount = s.credits_count || 0;
  // A player with 0 credits is conservative (5); 1-3 credits is healthy (7-10);
  // 4+ credits is concerning (gradually drops back to 4).
  let usageScore;
  if (creditCount === 0) usageScore = 5;
  else if (creditCount <= 3) usageScore = 7 + creditCount; // 8, 9, 10
  else usageScore = Math.max(4, 10 - (creditCount - 3) * 1.5);
  return score((csScore * 2 + usageScore) / 3);
}

function evalTraining(s) {
  // Trainings completed per driver + competence average.
  const perDriver = (s.drivers_trainings_total || 0) / Math.max(1, s.drivers_total || 1);
  const a = rangeScore(perDriver, 0, 2, 5);
  const b = rangeScore(s.drivers_avg_competence || 0, 0, 2.5, 4.5); // /5
  return score((a + b) / 2);
}

function evalSupport(s) {
  // Number of support roles hired + depot phase progression.
  const a = rangeScore(s.support_total || 0, 0, 3, 8);
  const b = rangeScore(s.depot_phase || 0, 0, 1, 3);
  const c = rangeScore(s.phase || 1, 1, 3, 5);
  return score((a + b + c) / 3);
}

function evalCRM(s) {
  // Recurring client base + active contracts depth.
  const a = rangeScore(s.client_loyalty_count || 0, 0, 3, 10);
  const b = rangeScore(s.contracts_active || 0, 0, 4, 12);
  const c = rangeScore(s.contracts_completed_lifetime || 0, 0, 10, 50);
  return score((a + b + c) / 3);
}

function evalRSE(s) {
  // Reputation + notoriety + electric fleet share.
  const a = rangeScore(s.reputation || 0, 30, 60, 85);
  const b = rangeScore(s.notoriety || 0, 0, 30, 70);
  const electricShare = (s.fleet_electric || 0) / Math.max(1, s.fleet_total || 1);
  const c = rangeScore(electricShare * 100, 0, 20, 50);
  return score((a + b + c) / 3);
}

/**
 * 17/06/2026 (Lot 6) — Axe Diplomatie & Relations externes.
 *
 * Mesure l'aisance de l'apprenant dans la gestion des relations
 * concurrentielles : équilibre rivaux/alliés, prise de décision sur les
 * événements narratifs (OPA, fusion, plainte), embauche d'un conseil
 * juridique, et activation de flags diplomatiques marquants.
 *
 * 3 composantes :
 *   a) Score moyen de relations : >55 = bon réseau ; <40 = isolement
 *   b) Décisions narratives prises (proxy d'engagement avec la dimension)
 *   c) Bonus conseil juridique embauché + flag fusion accepté
 */
function evalDiplomacy(s) {
  // a) Moyenne des relations avec les concurrents (centré sur 50 = neutre)
  const avgRel = s.competitor_relations_avg ?? 50;
  // Récompense les bonnes relations actives (>55), pénalise l'isolement (<40)
  const a = rangeScore(avgRel, 35, 55, 78);
  // b) Engagement décisionnel : nb d'événements narratifs résolus
  const decisions = s.relation_events_decided || 0;
  const b = rangeScore(decisions, 0, 3, 10);
  // c) Bonus si le conseil juridique est en poste + flags narratifs activés
  const flags = s.narrative_flags_count || 0;
  const legalBonus = s.legal_counsel_hired ? 2 : 0;
  const c = Math.min(10, rangeScore(flags, 0, 1, 4) + legalBonus);
  return score((a + b + c) / 3);
}

/* ─── Typology label (combination heuristic) ──────────────────────────── */

function buildTypology(scores) {
  const { riskTaking, finance, hr, banking, training, support, crm, rse, diplomacy } = scores;
  const avg = (riskTaking + finance + hr + banking + training + support + crm + rse + (diplomacy || 0)) / 9;
  const dominant = Object.entries(scores).sort((a, b) => b[1] - a[1])[0];
  const weakest = Object.entries(scores).sort((a, b) => a[1] - b[1])[0];

  // Priority order — first matching wins.
  if (avg < 3) return { id: 'demarrage', label: 'Démarrage', summary: "Profil en phase d'apprentissage initial. Les indicateurs sont encore peu différenciés." };
  if (riskTaking >= 7 && finance <= 4) return { id: 'aventurier', label: 'Aventurier impulsif', summary: "Forte appétence pour la croissance et l'endettement, mais la santé financière fragilisée appelle à la consolidation." };
  if (riskTaking >= 6 && finance >= 6) return { id: 'visionnaire', label: 'Visionnaire équilibré', summary: "Prend des décisions ambitieuses tout en gardant un cap financier maîtrisé. Profil le plus difficile à tenir dans la durée." };
  if (finance >= 7 && riskTaking <= 3) return { id: 'prudent', label: 'Prudent économe', summary: "Gestion saine et conservatrice. Risque : croissance ralentie, opportunités manquées." };
  if (hr >= 7 && (training >= 6 || support >= 6)) return { id: 'rh-driven', label: 'Patron RH-driven', summary: "Investit massivement dans son équipe (formations, support, satisfaction). Très bon pour la fidélisation, peut être lent à scaler." };
  if (banking >= 8 && finance >= 5) return { id: 'financier', label: 'Profil financier', summary: "Utilise le levier bancaire avec discernement. Solide score de crédit et endettement maîtrisé." };
  if (crm >= 7) return { id: 'commercial', label: 'Profil commercial', summary: "Excelle à acquérir et fidéliser des clients. Bonne base récurrente pour la stabilité du CA." };
  if (rse >= 7) return { id: 'rse', label: 'Profil RSE / image', summary: "Mise sur la réputation, la notoriété et la transition écologique. Stratégie de marque cohérente." };
  if (avg >= 6.5) return { id: 'gestionnaire', label: 'Gestionnaire complet', summary: "Aucun axe en grande faiblesse : profil équilibré et solide sur tous les fronts." };
  if (weakest[1] <= 2 && dominant[1] >= 7) return { id: 'specialise', label: 'Spécialiste mono-axe', summary: `Performance marquée sur "${humanLabel(dominant[0])}" mais déséquilibre fort sur "${humanLabel(weakest[0])}" à corriger.` };
  return { id: 'developpement', label: 'En développement', summary: "Profil intermédiaire en progression. Plusieurs axes méritent d'être renforcés pour franchir un palier." };
}

/* ─── Pedagogical analysis bullets ────────────────────────────────────── */

function buildAnalysisLines(snapshot, scores, typology) {
  const lines = [];
  // Opening sentence — typology framing.
  lines.push(`Au jour ${snapshot.total_days || 0} de la simulation, l'apprenant·e présente un profil « ${typology.label} ». ${typology.summary}`);

  // Financial health line.
  const burn = Math.max(1, snapshot.monthly_expenses || 1);
  const runwayMonths = (snapshot.treasury || 0) / burn;
  const equity = snapshot.equity || 0;
  if (equity < 0) {
    lines.push(`Les capitaux propres sont négatifs (${Math.round(equity).toLocaleString('fr-FR')} €), traduisant un cumul de pertes qui dépasse les apports. Une recapitalisation ou un retour à la rentabilité est nécessaire.`);
  } else if (runwayMonths < 2) {
    lines.push(`La trésorerie ne couvre que ${runwayMonths.toFixed(1)} mois de charges courantes. Une vigilance immédiate sur les sorties est impérative.`);
  } else if (runwayMonths > 8) {
    lines.push(`La trésorerie représente ${runwayMonths.toFixed(1)} mois de charges : posture financière saine, mais attention aux liquidités inutilisées qui auraient pu financer l'expansion.`);
  } else {
    lines.push(`Le coussin de trésorerie (${runwayMonths.toFixed(1)} mois) reste correct sans être confortable. À surveiller en cas de cycle bas.`);
  }

  // HR commentary.
  const sat = snapshot.drivers_avg_satisfaction || 0;
  const drivers = snapshot.drivers_total || 0;
  if (drivers === 0) {
    lines.push("Aucun chauffeur recruté à ce stade : la dimension RH du jeu reste à explorer.");
  } else if (sat >= 70) {
    lines.push(`L'équipe de ${drivers} chauffeur·s affiche une satisfaction élevée (${sat}%), gage de fidélisation et de productivité.`);
  } else if (sat < 50) {
    lines.push(`La satisfaction moyenne des chauffeurs est faible (${sat}%) : risque accru de démissions et de baisse de productivité.`);
  } else {
    lines.push(`L'équipe de ${drivers} chauffeur·s présente une satisfaction moyenne (${sat}%) : marge de progression possible via formations et politique salariale.`);
  }

  // Risk & banking commentary.
  const cs = snapshot.credit_score;
  const debt = snapshot.credits_total_remaining || 0;
  if (debt === 0 && (snapshot.total_days || 0) > 90) {
    lines.push("L'absence totale de recours au crédit après 3 mois traduit un choix de prudence — au risque de freiner la croissance via l'effet de levier.");
  } else if (debt > 0 && cs != null && cs >= 70) {
    lines.push(`Le score de crédit (${cs}/100) reflète une gestion bancaire mature : endettement maîtrisé et remboursements honorés.`);
  } else if (debt > 0 && cs != null && cs < 50) {
    lines.push(`Score de crédit faible (${cs}/100). Examiner les retards de paiement et la concentration de la dette.`);
  } else if (debt > 0) {
    lines.push(`L'apprenant·e utilise le levier bancaire (${(debt).toLocaleString('fr-FR')} € de dette résiduelle) avec un score moyen — à surveiller.`);
  }

  // Strongest / weakest axis closure.
  const sorted = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  const strong = sorted[0];
  const weak = sorted[sorted.length - 1];
  if (strong[1] - weak[1] >= 4) {
    lines.push(`Axe le plus solide : ${humanLabel(strong[0])} (${strong[1]}/10). Axe à travailler en priorité : ${humanLabel(weak[0])} (${weak[1]}/10).`);
  }

  // 17/06/2026 (Lot 6) — Commentaire Diplomatie / Relations externes.
  const allies = snapshot.competitor_allies_count || 0;
  const enemies = snapshot.competitor_enemies_count || 0;
  const decisions = snapshot.relation_events_decided || 0;
  if (decisions === 0 && (snapshot.total_days || 0) > 60) {
    lines.push("La dimension relationnelle avec les concurrents reste inexplorée : aucune décision narrative n'a été prise à ce stade. Les événements (propositions de fusion, plaintes, débauchages) arrivent par la Messagerie — y répondre façonne alliances et inimitiés.");
  } else if (enemies >= 2 && allies === 0) {
    lines.push(`L'apprenant·e accumule les inimitiés (${enemies} ennemis, 0 allié). Vigilance : exposition aux OPA et plaintes en concurrence déloyale. Une fonction support juridique serait pertinente.`);
  } else if (allies >= 2) {
    lines.push(`Réseau diplomatique solide : ${allies} alliés cultivés. Limite l'exposition aux attaques (OPA, plaintes) et ouvre la porte aux propositions de fusion. Bonne posture stratégique.`);
  } else if (snapshot.legal_counsel_hired) {
    lines.push("Le recrutement d'un conseil juridique (Me Charron-Berthet) sécurise la gestion des procédures et atténue les pénalités. Décision prudente.");
  }

  return lines.slice(0, 6);
}

/* ─── Highlights for the PDF box ──────────────────────────────────────── */

function buildHighlights(snapshot, scores) {
  const positives = [];
  const improvements = [];

  if (scores.finance >= 7) positives.push("Gestion financière maîtrisée");
  if (scores.hr >= 7) positives.push("Équipe motivée et bien dimensionnée");
  if (scores.training >= 7) positives.push("Forte culture de la formation");
  if (scores.crm >= 7) positives.push("Portefeuille client récurrent solide");
  if (scores.rse >= 7) positives.push("Démarche RSE / réputation valorisée");
  if (scores.banking >= 8) positives.push("Excellent score bancaire");
  if (scores.support >= 7) positives.push("Fonctions support bien développées");
  if (scores.diplomacy >= 7) positives.push("Réseau concurrentiel maîtrisé / décisions narratives engagées");
  if ((snapshot.contracts_completed_lifetime || 0) >= 30) positives.push(`${snapshot.contracts_completed_lifetime} contrats clôturés avec succès`);

  if (scores.finance <= 3) improvements.push("Renforcer la trésorerie ou réduire les charges fixes");
  if (scores.hr <= 3) improvements.push("Investir dans la satisfaction / formation des chauffeurs");
  if (scores.crm <= 3) improvements.push("Diversifier et fidéliser la clientèle");
  if (scores.training <= 3) improvements.push("Programmer plus de formations métier (FCO, CACES, ADR…)");
  if (scores.banking <= 3 && (snapshot.credits_count || 0) > 0) improvements.push("Examiner les retards de remboursement");
  if (scores.support <= 3 && (snapshot.total_days || 0) > 60) improvements.push("Recruter du support (compta, atelier, commerce…)");
  if (scores.rse <= 3) improvements.push("Travailler l'image de marque et l'éco-responsabilité");
  if (scores.diplomacy <= 3 && (snapshot.total_days || 0) > 90) improvements.push("Activer la dimension relationnelle (répondre aux événements concurrents, conseil juridique)");
  if ((snapshot.equity || 0) < 0) improvements.push("Capitaux propres négatifs — recapitaliser");

  return {
    positives: positives.slice(0, 3),
    improvements: improvements.slice(0, 3),
  };
}

/* ─── Smart recommendations ───────────────────────────────────────────── */

/**
 * Build a list of 3-5 actionable, context-aware recommendations for the
 * apprentice. Rule-based (deterministic) so the same snapshot always
 * produces the same advice — important for pedagogical traceability.
 *
 * Each recommendation: { id, level: 'critical'|'warning'|'tip'|'opportunity',
 *                        title, body }
 *
 * Priority order (first matching wins per category):
 *   1. Existential threats (insolvency, runway < 1 month) → critical
 *   2. Warnings (low runway, saturated HR, debt drift)    → warning
 *   3. Opportunities (high credit score, idle cash)        → opportunity
 *   4. Tips (gentle nudges — formations, RSE)              → tip
 *
 * We always return at least 1 recommendation (a generic encouragement if
 * the profile is balanced) and cap at 5 so the PDF page stays clean.
 */
function buildRecommendations(snapshot, scores) {
  const s = snapshot || {};
  const recs = [];
  const burn = Math.max(1, s.monthly_expenses || 1);
  const runway = (s.treasury || 0) / burn;
  const days = s.total_days || 0;
  const drivers = s.drivers_total || 0;
  const debt = s.credits_total_remaining || 0;
  const creditCount = s.credits_count || 0;
  const credScore = s.credit_score;
  const sat = s.drivers_avg_satisfaction || 0;
  const fatigue = s.drivers_avg_fatigue || 0;
  const trainingsPerDriver = drivers > 0 ? (s.drivers_trainings_total || 0) / drivers : 0;
  const electricShare = (s.fleet_electric || 0) / Math.max(1, s.fleet_total || 1);
  const fleetTotal = s.fleet_total || 0;
  const activeContracts = s.contracts_active || 0;
  const equity = s.equity || 0;
  const loyalty = s.client_loyalty_count || 0;
  const reputation = s.reputation || 0;
  const notoriety = s.notoriety || 0;
  const support = s.support_total || 0;

  /* — 1. Existential threats (level: critical) — */
  if (equity < 0) {
    recs.push({
      id: 'rec-equity-negative',
      level: 'critical',
      title: 'Capitaux propres négatifs',
      body: "Vos pertes cumulées dépassent les apports. Réinjectez des fonds propres ou redressez la rentabilité avant que la situation ne s'aggrave (commissaire aux comptes, alerte). Examinez vos contrats déficitaires et coupez les charges fixes non-essentielles.",
    });
  } else if (runway < 1 && days > 30) {
    recs.push({
      id: 'rec-runway-critical',
      level: 'critical',
      title: 'Trésorerie critique',
      body: `Moins d'un mois de charges en réserve (${runway.toFixed(1)} mois). Décrochez immédiatement 1-2 contrats récurrents, suspendez tout investissement, et envisagez un crédit court terme si votre score bancaire le permet.`,
    });
  }

  /* — 2. Warnings (level: warning) — */
  if (recs.length === 0 || recs[0].level !== 'critical') {
    if (runway < 2.5 && days > 30) {
      recs.push({
        id: 'rec-runway-low',
        level: 'warning',
        title: 'Attention à la trésorerie',
        body: `Votre coussin ne couvre que ${runway.toFixed(1)} mois de charges. Stabilisez le CA avec des contrats récurrents ou réduisez les charges fixes (loyer, leasing) avant d'envisager une expansion.`,
      });
    }
  }
  if (sat < 50 && drivers >= 2) {
    recs.push({
      id: 'rec-hr-satisfaction',
      level: 'warning',
      title: 'Climat social fragilisé',
      body: `Satisfaction moyenne des chauffeurs à ${sat}%. Risque de démissions : revalorisez les salaires, espacez les missions difficiles, ou recrutez un Responsable RH pour fluidifier la gestion d'équipe.`,
    });
  }
  if (fatigue > 70 && drivers >= 2) {
    recs.push({
      id: 'rec-hr-fatigue',
      level: 'warning',
      title: 'Fatigue élevée de l\'équipe',
      body: `Fatigue moyenne à ${fatigue}%. Imposez des jours de repos, alternez les chauffeurs sur les missions intensives, ou recrutez pour absorber la charge avant les premiers burnouts.`,
    });
  }
  if (creditCount >= 4 && credScore != null && credScore < 55) {
    recs.push({
      id: 'rec-debt-drift',
      level: 'warning',
      title: 'Endettement à surveiller',
      body: `${creditCount} crédits actifs avec un score bancaire moyen (${credScore}/100). Évitez tout nouvel emprunt, soldez le crédit le plus coûteux par anticipation si la trésorerie le permet.`,
    });
  }
  if (activeContracts === 0 && days > 30) {
    recs.push({
      id: 'rec-no-contracts',
      level: 'warning',
      title: 'Aucun contrat actif',
      body: 'Sans contrat en cours, votre flotte génère des charges sans CA. Acceptez immédiatement un contrat ponctuel pour relancer la machine, puis sécurisez 1-2 contrats récurrents.',
    });
  }

  /* — 3. Opportunities (level: opportunity) — */
  if (credScore != null && credScore >= 75 && fleetTotal < 8 && runway >= 3) {
    recs.push({
      id: 'rec-leverage-credit',
      level: 'opportunity',
      title: 'Profitez de votre score bancaire',
      body: `Excellent score bancaire (${credScore}/100). Si votre stratégie l'exige, vous pouvez financer 1-2 véhicules supplémentaires via crédit : l'effet de levier est désormais très favorable.`,
    });
  } else if (runway > 10 && fleetTotal < 6) {
    recs.push({
      id: 'rec-idle-cash',
      level: 'opportunity',
      title: 'Trésorerie sous-investie',
      body: `${runway.toFixed(1)} mois de runway : c'est sain mais sur-dimensionné. Cette liquidité dort — elle pourrait financer un véhicule supplémentaire, une formation collective ou un investissement TMS.`,
    });
  }
  if (reputation >= 75 && notoriety < 50) {
    recs.push({
      id: 'rec-notoriety-gap',
      level: 'opportunity',
      title: 'Capitalisez sur votre réputation',
      body: `Réputation forte (${reputation}/100) mais notoriété en retrait (${notoriety}/100). Visez les Appels d'Offres régionaux/nationaux : votre image est prête, il vous manque juste la visibilité.`,
    });
  }
  if (loyalty >= 5 && activeContracts < 6) {
    recs.push({
      id: 'rec-loyalty-leverage',
      level: 'opportunity',
      title: 'Activez votre base fidèle',
      body: `${loyalty} clients récurrents : excellente base. Utilisez les actions clients (page Clients) — visite commerciale, sollicitation de mission — pour générer des contrats dédiés au coût d'acquisition quasi nul.`,
    });
  }

  /* — 4. Tips (level: tip) — */
  if (trainingsPerDriver < 1 && drivers > 0 && days > 60) {
    recs.push({
      id: 'rec-trainings',
      level: 'tip',
      title: 'Pensez aux formations',
      body: `Ratio formations/chauffeur faible (${trainingsPerDriver.toFixed(1)}). Une FCO + un CACES débloquent les contrats spécialisés à meilleure marge, et la satisfaction de l'équipe grimpe mécaniquement.`,
    });
  }
  if (electricShare < 0.15 && fleetTotal >= 3) {
    recs.push({
      id: 'rec-rse-electric',
      level: 'tip',
      title: 'Verdir votre flotte',
      body: `Part électrique faible (${Math.round(electricShare * 100)}%). Un véhicule électrique en zone urbaine améliore réputation, notoriété et donne accès aux AO RSE plus rémunérateurs.`,
    });
  }
  if (support === 0 && days > 60 && fleetTotal >= 3) {
    recs.push({
      id: 'rec-hire-support',
      level: 'tip',
      title: 'Structurez le support',
      body: 'Aucune fonction support recrutée. Un Responsable d\'exploitation ou un Comptable libère du temps de pilotage et débloque des bonus durables (réputation, vitesse de décision).',
    });
  }
  if (scores.training >= 7 && scores.crm <= 4 && days > 90) {
    recs.push({
      id: 'rec-balance-crm',
      level: 'tip',
      title: 'Équilibrez formation et commercial',
      body: 'Équipe très qualifiée mais portefeuille client à diversifier. Démarchez activement, participez aux Appels d\'Offres : votre capacité opérationnelle est prête à scaler.',
    });
  }

  // Always provide at least 1 line of feedback.
  if (recs.length === 0) {
    recs.push({
      id: 'rec-balanced',
      level: 'tip',
      title: 'Profil équilibré — continuez',
      body: 'Aucun axe en alerte. Continuez sur votre lancée : visez maintenant un palier (phase suivante, AO premium, dépôt agrandi) pour franchir la marche supérieure.',
    });
  }

  // Sort by level severity (critical → warning → opportunity → tip), then
  // cap to 5 for the PDF layout.
  const order = { critical: 0, warning: 1, opportunity: 2, tip: 3 };
  recs.sort((a, b) => order[a.level] - order[b.level]);
  return recs.slice(0, 5);
}

/* ─── Public mapping for axis labels (used by the radar + PDF) ────────── */

export const AXIS_LABELS = {
  riskTaking: 'Prise de risque',
  finance: 'Gestion financière',
  hr: 'Gestion humaine',
  banking: 'Gestion bancaire',
  training: 'Formation',
  support: 'Fonctions support',
  crm: 'CRM / Clients',
  rse: 'RSE / Conformité',
  diplomacy: 'Diplomatie & Relations externes',
};
export const AXIS_ORDER = ['riskTaking', 'finance', 'hr', 'banking', 'training', 'support', 'crm', 'rse', 'diplomacy'];

// 28/07/2026 — Libellés courts pour le radar du PDF (les longs libellés
// « Diplomatie & Relations externes » / « RSE / Conformité » débordaient
// à gauche de la page).
export const AXIS_LABELS_SHORT = {
  riskTaking: 'Prise de risque',
  finance: 'Finance',
  hr: 'RH',
  banking: 'Banque',
  training: 'Formation',
  support: 'Support',
  crm: 'Clients',
  rse: 'RSE',
  diplomacy: 'Diplomatie',
};

function humanLabel(axisId) { return AXIS_LABELS[axisId] || axisId; }

/* ─── Public API ──────────────────────────────────────────────────────── */

export default function computePlayerTypology(snapshot) {
  const s = snapshot || {};
  const scores = {
    riskTaking: evalRiskTaking(s),
    finance:    evalFinance(s),
    hr:         evalHR(s),
    banking:    evalBanking(s),
    training:   evalTraining(s),
    support:    evalSupport(s),
    crm:        evalCRM(s),
    rse:        evalRSE(s),
    diplomacy:  evalDiplomacy(s),
  };
  const typology = buildTypology(scores);
  const analysis = buildAnalysisLines(s, scores, typology);
  const highlights = buildHighlights(s, scores);
  const recommendations = buildRecommendations(s, scores);
  return { scores, typology, analysis, highlights, recommendations };
}
